import { motion } from "framer-motion";
import { tokens } from "@/art/tokens";

interface SpinnerProps {
    size?: number;
    color?: string;
    label?: string;
}

/**
 * 공용 로딩 인디케이터. 양피지 테마에 맞춘 원호 회전 + 선택적 라벨.
 *
 * 사용처:
 * - RunResult busy 상태 ("운명의 실이 여물어갑니다…")
 * - 향후 네트워크 대기 중인 모든 씬 (HomeScene continue, RunForge retry 등)
 */
export function Spinner({ size = 48, color = tokens.color.ember, label }: SpinnerProps) {
    const stroke = Math.max(2, Math.round(size / 16));
    const radius = size / 2 - stroke;
    const circumference = 2 * Math.PI * radius;
    const innerRadius = radius * 0.6;
    const innerCircumference = 2 * Math.PI * innerRadius;
    // 2026-04-23: Spirytale 느낌의 rune dots — 팔방위 점. 외곽 arc 와 함께 회전.
    const runeDots = Array.from({ length: 8 }, (_, i) => {
        const angle = (Math.PI * 2 * i) / 8 - Math.PI / 2;
        return {
            x: size / 2 + (radius + stroke * 1.6) * Math.cos(angle),
            y: size / 2 + (radius + stroke * 1.6) * Math.sin(angle),
        };
    });

    return (
        <div className="flex flex-col items-center gap-s">
            <div className="relative" style={{ width: size, height: size }}>
                {/* 외곽 회전 arc + rune dots */}
                <motion.svg
                    width={size}
                    height={size}
                    viewBox={`0 0 ${size} ${size}`}
                    animate={{ rotate: 360 }}
                    transition={{ duration: 1.4, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0"
                    aria-hidden
                    style={{ filter: `drop-shadow(0 0 6px ${color}66)` }}
                >
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={`${color}22`}
                        strokeWidth={stroke}
                    />
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={radius}
                        fill="none"
                        stroke={color}
                        strokeWidth={stroke}
                        strokeLinecap="round"
                        strokeDasharray={circumference}
                        strokeDashoffset={circumference * 0.72}
                    />
                    {runeDots.map((d, i) => (
                        <circle
                            key={i}
                            cx={d.x}
                            cy={d.y}
                            r={stroke * 0.5}
                            fill={color}
                            opacity={i === 0 ? 0.9 : 0.3}
                        />
                    ))}
                </motion.svg>
                {/* 내부 역방향 회전 arc — 깊이감 부여 */}
                <motion.svg
                    width={size}
                    height={size}
                    viewBox={`0 0 ${size} ${size}`}
                    animate={{ rotate: -360 }}
                    transition={{ duration: 2.2, repeat: Infinity, ease: "linear" }}
                    className="absolute inset-0"
                    aria-hidden
                >
                    <circle
                        cx={size / 2}
                        cy={size / 2}
                        r={innerRadius}
                        fill="none"
                        stroke={color}
                        strokeWidth={Math.max(1, stroke * 0.6)}
                        strokeLinecap="round"
                        strokeDasharray={innerCircumference}
                        strokeDashoffset={innerCircumference * 0.88}
                        opacity={0.55}
                    />
                </motion.svg>
                {/* 중앙 pulse — "생각 중" 감 */}
                <motion.div
                    aria-hidden
                    className="absolute rounded-full"
                    animate={{ opacity: [0.25, 0.75, 0.25], scale: [0.8, 1.1, 0.8] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    style={{
                        left: "50%",
                        top: "50%",
                        width: size * 0.18,
                        height: size * 0.18,
                        marginLeft: -size * 0.09,
                        marginTop: -size * 0.09,
                        background: `radial-gradient(circle, ${color}, ${color}00 70%)`,
                        boxShadow: `0 0 ${size * 0.25}px ${color}66`,
                    }}
                />
            </div>
            {label && (
                <div
                    className="font-body"
                    style={{
                        color: tokens.color.parchmentDim,
                        fontSize: tokens.type.size.small,
                        letterSpacing: tokens.type.letterSpacing.wide,
                    }}
                    role="status"
                    aria-live="polite"
                >
                    {label}
                </div>
            )}
        </div>
    );
}
